<!-- Component can be used in microscope and periscope -->
<template>
  <LabelAndHint
    v-slot="{ inputId, ariaLabelledby, isSuccess, isWarning, isErr }"
    v-bind="{ ...props, error: props.error || invalid, errorMessages: props.errorMessages || errorMessages }"
  >
    <div
      class="input-wrapper"
      :class="{
        'input-success': isSuccess,
        'input-warning': isWarning,
        'input-error': isErr,
        'input-disabled': disabled,
        borderless: borderless,
        slim: slim,
        'text-align-right': textAlignRight,
      }"
      :data-color="color"
    >
      <input
        :id="inputId"
        ref="inputElement"
        :aria-labelledby="ariaLabelledby"
        :value="inputValue"
        type="text"
        :inputmode="mode"
        :placeholder="placeholder"
        :autocomplete="autocompleteOff ? 'off' : undefined"
        :disabled="disabled"
        @input="onInput"
        @keydown="emit('keydown', $event)"
        @focus="emit('focus')"
        @blur="onBlur"
      />
      <span v-if="suffix">
        {{ suffix }}
      </span>
      <div v-if="$slots.icon" class="icon">
        <slot name="icon" />
      </div>
      <div v-if="$slots.append" class="append">
        <slot name="append" />
      </div>
    </div>
  </LabelAndHint>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, watch } from 'vue'

import { formatAmount, formatDate, formatDecimalAmount, type ValidationRuleObject } from '@staff/shared'
import { useCursorPositioning, useInputValidation } from '../../composables'

import LabelAndHint from './LabelAndHint.vue'

interface AppTextFieldProps {
  id?: string | number
  type?: 'text' | 'amount' | 'date' | 'amountWithDecimals'
  textAlignRight?: boolean

  // For shared components to work in both vue 2.7 and vue 3 we need to support both `value` and `modelValue`
  // When periscope is updated to vue 3, we can remove `value` and only use `modelValue`
  modelValue?: string | number | null
  value?: string | number | null

  modelModifiers?: {
    number?: boolean
  }

  label?: string
  placeholder?: string

  hint?: string | string[]
  suffix?: string

  borderless?: boolean
  slim?: boolean

  // Trims leading/trailing whitespace on blur.
  // Note: do NOT use `v-model.trim` for this — Vue applies that modifier inside `emit()` on every
  // `update:modelValue`, i.e. on every keystroke, which strips the space you just typed as soon as
  // anything re-renders the input (see the forced `value` patch in runtime-core's patchElement).
  trim?: boolean

  success?: boolean
  successMessages?: string | string[]

  warning?: boolean
  warningMessages?: string | string[]

  error?: boolean
  errorMessages?: string | string[]

  disabled?: boolean
  labelForMultipleInputs?: boolean

  inputMode?: 'numeric' | 'email' | 'tel' | 'text' | 'decimal'
  color?: 'white'

  rules?: (ValidationRuleObject | null)[]
  validationOptions?: {
    validateOnModelChange?: boolean
  }
  autocompleteOff?: boolean
}

const props = defineProps<AppTextFieldProps>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | number): void
  (e: 'input', value: string | number): void
  (e: 'focus'): void
  (e: 'blur'): void
  (e: 'keydown', value: KeyboardEvent): void
}>()

const mode = computed(() => props.inputMode ?? 'text')
const currentValue = computed(() => props.modelValue ?? props.value)

const inputValue = computed(() => {
  if (props.type === 'amount') {
    return formatAmount(currentValue.value)
  } else if (props.type === 'amountWithDecimals') {
    return formatDecimalAmount(currentValue.value)
  } else if (props.type === 'date' && currentValue.value) {
    return formatDate(currentValue.value.toString(), 'YYYY-MM-DD')
  } else {
    return currentValue.value
  }
})

const inputElement = ref<HTMLInputElement | null | undefined>(undefined)

if (props.type === 'amount') {
  useCursorPositioning(inputElement)
} else if (props.type === 'amountWithDecimals') {
  useCursorPositioning(inputElement, true)
}

const { invalid, errorMessages, validate } = useInputValidation(
  () => inputValue.value,
  () => props.rules,
  inputElement,
)

const emitValue = (raw: string) => {
  let value: string | number = raw

  if (props.type === 'amount') {
    value = raw.replace(/\D/g, '')
  }

  if (props.type === 'amountWithDecimals') {
    const normalized = raw.replace(/,/g, '.')
    const digitsAndDot = normalized.replace(/[^\d.]/g, '')
    value = digitsAndDot.replace(/(\..*)\./g, '$1')
  }

  if (props.type === 'date') {
    value = formatDate(raw, 'YYYY-MM-DD')
  }

  if (props.modelModifiers?.number && typeof value === 'string') {
    if (value === '') {
      value = ''
    } else {
      const parsedValue = Number(value)
      value = Number.isNaN(parsedValue) ? value : parsedValue
    }
  }

  emit('update:modelValue', value)
  emit('input', value)

  if ((props.rules?.length ?? 0) > 0) {
    nextTick(() => validate())
  }
}

const onInput = (v: Event) => {
  if (!(v.target instanceof HTMLInputElement)) {
    return
  }

  emitValue(v.target.value)
}

const onBlur = () => {
  const element = inputElement.value

  if (props.trim && element) {
    const trimmed = element.value.trim()

    if (trimmed !== element.value) {
      // Write to the DOM first: if the trimmed value equals the model, no re-render is triggered
      // and nothing else would clear the whitespace from the input.
      element.value = trimmed
      emitValue(trimmed)
    }
  }

  emit('blur')

  if ((props.rules?.length ?? 0) > 0) {
    nextTick(() => validate())
  }
}

watch(
  () => inputValue.value,
  () => {
    if (invalid.value || props.validationOptions?.validateOnModelChange) {
      nextTick(() => validate())
    }
  },
)

// Native `autofocus` only reliably fires on the initial page load, not for fields
// mounted later (e.g. inside a modal opened by a click) - callers needing that
// should call this explicitly once the field is mounted.
defineExpose({
  focus: () => inputElement.value?.focus(),
})
</script>

<style lang="scss" scoped>
.input-wrapper {
  display: flex;
  align-items: center;
  gap: 4px;

  &[data-color='white'] {
    background-color: white !important;
  }

  span {
    margin-right: 12px;
    color: var(--color-fg-subtle);
  }

  border: 1px solid var(--color-border-default);
  border-radius: 6px;

  &::placeholder {
    color: var(--color-fg-subtle);
  }

  &:focus-within {
    border-color: var(--color-accent-fg);
    outline: 2px solid var(--color-accent-fg);
    outline-offset: -2px;

    &.input-error {
      outline: 2px solid var(--color-danger-fg);
    }
  }

  &.input-success {
    border-color: var(--color-textfield-success-border);
    background-color: var(--color-textfield-success-bg);
  }

  &.input-warning {
    border-color: var(--color-textfield-warning-border);
    background-color: var(--color-textfield-warning-bg);
  }

  &.input-error {
    border-color: var(--color-danger-fg);
  }

  &.input-disabled {
    cursor: not-allowed;
    border-color: var(--color-fg-disabled);

    input {
      cursor: not-allowed;
      color: var(--color-fg-disabled);

      &::placeholder {
        color: var(--color-fg-disabled);
      }
    }
    span {
      color: var(--color-fg-disabled);
    }
  }

  &.text-align-right {
    input {
      text-align: right;
    }
  }

  &.borderless {
    border: none;
    outline: none;
  }

  &.slim {
    input {
      padding: 1px;
      font-size: 12px;
      border-radius: 3px;
    }
  }
}

input {
  display: block;
  width: 100%;
  padding: 6px 12px;

  color: var(--color-fg-default);
  font-size: 16px;

  background-color: transparent;
  border: none;

  &:focus-visible {
    outline: none;
  }
}

.icon {
  padding: 4px;
}

.append {
  margin: auto;
}
</style>
